import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { MatrixCareCensusDto, MatrixCareCensusGroupDto, MatrixCarePatientCensusFilterDto, MatrixCarePatientClient, UnlinkMatrixCarePatientDto } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { censusSlice } from '../census/slice';
import { initialState, MatrixCareCensusState } from './models';

export const matrixCareCensusSlice = createSlice({
    name: 'matrix-care-census',
    initialState,
    reducers: {
        setLoading: (state: MatrixCareCensusState, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            state.processing = false;
        },

        setProcessing: (state: MatrixCareCensusState, action: PayloadAction<boolean>) => {
            state.loading = false;
            state.processing = action.payload;
        },

        setError: (state: MatrixCareCensusState, action: PayloadAction<string>) => {
            state.errorMessage = action.payload;
            state.loading = false;
            state.processing = false;
        },

        setCensus: (state: MatrixCareCensusState, action: PayloadAction<MatrixCareCensusGroupDto>) => {
            state.census = action.payload;
            state.loading = false;
            state.processing = false;
            state.errorMessage = null;
        },

        setCurrentMatrixCareCensus: (state: MatrixCareCensusState, action: PayloadAction<MatrixCareCensusDto>) => {
            state.currentCensus = action.payload;
        },

        setCensusFilters: (state: MatrixCareCensusState, action: PayloadAction<MatrixCarePatientCensusFilterDto>) => {
            state.censusFilters = action.payload;
        },

        updateCensus: (state: MatrixCareCensusState, action: PayloadAction<MatrixCareCensusDto>) => {
            const { patientId } = action.payload;

            if (state.census?.items != null) {
                const index = state.census.items.findIndex((x) => x.patientId === patientId);

                let newItems = [...state.census.items];

                if (index !== -1) {
                    newItems = newItems.map((item) => {
                        if (item.patientId === patientId) {
                            return { ...item, ...action.payload } as MatrixCareCensusDto;
                        }

                        return item;
                    });
                } else {
                    newItems.push(action.payload);

                    // Match ordering from server side logic
                    newItems = orderBy(newItems, ['lastName', 'firstName', 'middleName', (x) => x.birthDate?.format('L')], ['asc', 'asc', 'asc', 'asc']);
                }

                const newCensus = { ...state.census, items: newItems } as MatrixCareCensusGroupDto;

                state.census = newCensus;
            }
        },
    },
});

export function refreshMatrixCareCensus(): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const { censusFilters } = getState().matrixCareCensus;

        if (censusFilters != null) {
            await dispatch(fetchMatrixCareCensus(censusFilters.facilityId, true));
        }
    };
}

export function fetchMatrixCareCensus(matrixCareFacilityId: number, isRefresh = false): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (!isRefresh) {
            dispatch(matrixCareCensusSlice.actions.setLoading(true));
        }

        const client = new MatrixCarePatientClient(null, Axios);

        try {
            const filter = MatrixCarePatientCensusFilterDto.fromJS({
                facilityId: matrixCareFacilityId,
            });

            dispatch(matrixCareCensusSlice.actions.setCensusFilters(filter));

            const response = await client.census(filter);

            if (response.result.succeeded) {
                dispatch(matrixCareCensusSlice.actions.setCensus(response.result.entity));
            } else {
                handleError(response, () => dispatch(matrixCareCensusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(matrixCareCensusSlice.actions.setError(error.toString())));
        }
    };
}

export function setCurrentMatrixCareCensus(item?: MatrixCareCensusDto, visible = false): AppThunk {
    return async (dispatch: AppDispatch) => {
        if (visible) {
            dispatch(matrixCareCensusSlice.actions.setCurrentMatrixCareCensus(item));
        } else {
            dispatch(matrixCareCensusSlice.actions.setCurrentMatrixCareCensus(null));

            // Fix for: https://switchbox.teamwork.com/#/projects/568400/tasks/29909374
            dispatch(censusSlice.actions.setMatrixCarePatient(null));
        }
    };
}

export function unlinkMatrixCarePatient(item: MatrixCareCensusDto): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new MatrixCarePatientClient(null, Axios);

        try {
            const dto = UnlinkMatrixCarePatientDto.fromJS({
                patientIntakeId: item.patientIntakeId,
            });

            const response = await client.unlinkMatrixCarePatient(dto);

            if (response.result.succeeded) {
                item.isPatientLinked = false;
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

export const selectMatrixCareLoading = (state: RootState) => state.matrixCareCensus.loading;
export const selectMatrixCareProcessing = (state: RootState) => state.matrixCareCensus.processing;
export const selectMatrixCareError = (state: RootState) => state.matrixCareCensus.errorMessage;
export const selectMatrixCareCensus = (state: RootState) => state.matrixCareCensus.census;
export const selectMatrixCareCurrentCensus = (state: RootState) => state.matrixCareCensus.currentCensus;

export default matrixCareCensusSlice.reducer;
